import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface TokenUsageData {
  subscription_token_count: number;
  per_day_usage: Record<number, number>;
  peak_daily_usage: number;
  peak_day: number | null;
  remaining_tokens: number;
  has_subscription: boolean;
}

export interface QueueSummaryData {
  total_active_queues: number;
  total_earnings_today: number;
}

export interface ActiveSubscriptionsData {
  subscriptions: Array<{
    id: string;
    plan_type: string;
    provider_count: number;
    token_count: number;
    amount: number;
    status: string;
    start_date: string;
    end_date: string;
    quantity: number;
    plan_name: string | null;
    plan_type_category: string | null;
    payment_retry_count: number;
    last_payment_failure_at: string | null;
    grace_period_end: string | null;
  }>;
  totals: {
    total_providers: number;
    total_tokens: number;
    total_monthly_cost: number;
  };
}

export const useDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [tokenUsage, setTokenUsage] = useState<TokenUsageData | null>(null);
  const [queueSummary, setQueueSummary] = useState<QueueSummaryData | null>(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState<ActiveSubscriptionsData | null>(null);
  const [teamCount, setTeamCount] = useState<number>(0);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [subsRes, tokenRes, summaryRes, teamRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.GET_ACTIVE_SUBSCRIPTIONS).catch(() => null),
        axiosInstance.get(API_ENDPOINTS.GET_TOKEN_USAGE).catch(() => null),
        axiosInstance.get(API_ENDPOINTS.GET_QUEUE_SUMMARY).catch(() => null),
        axiosInstance.get(`${API_ENDPOINTS.GET_TEAM_MEMBERS}?page=1&limit=1`).catch(() => null),
      ]);

      if (subsRes?.data?.data) {
        setActiveSubscriptions(subsRes.data.data);
      }
      if (tokenRes?.data?.data) {
        setTokenUsage(tokenRes.data.data);
      }
      if (summaryRes?.data?.data) {
        setQueueSummary(summaryRes.data.data);
      }
      if (teamRes?.data?.data?.pagination) {
        setTeamCount(teamRes.data.data.pagination.total || 0);
      } else if (teamRes?.data?.pagination) {
        setTeamCount(teamRes.data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    tokenUsage,
    queueSummary,
    activeSubscriptions,
    teamCount,
    fetchDashboardData,
  };
};
